import { Component } from "react"

/* Modal */
import Modal from "react-modal"

/* Redux */
import { connect } from "react-redux"

/* REST API */
import { password as change } from "../../api/Profile"

/* Components */
import { Input } from "../../components"

/* Icons */
import { CloseIcon, CheckIcon, LockIcon } from "../../components/Icons"
import { LoadingOutlined } from "@ant-design/icons"

/* Helpers */
import { utils } from "../../helpers"

/* Sweet Alert */
import Swal from "../../helpers/alert"

/* Translate */
import { t } from "../../translates/translate"


/* THE STATE OF A CLOSED PANEL */
const EMPTY = {
    password: "",
    newPassword: "",
    repeatPassword: "",
    waiting: false,
}

/* THE SHORTEST PASSWORD THE SERVER ACCEPTS */
const LENGTH = 8


/* Widget Password */
class Password extends Component {

    constructor() {
        super()

        this.state = { visible: false, ...EMPTY }
    }

    /* Open */
    open = () => this.setState({ visible: true, ...EMPTY })

    /* Close */
    close = () => this.setState({ visible: false, ...EMPTY })


    /* RULES OF A NEW PASSWORD — THE VERY SAME LIST FEEDS THE HINTS AND THE STRENGTH BAR */
    rules = () => {

        /* Fields */
        const { newPassword, password } = this.state

        /* Send Result */
        return [
            { text: t("profile.rule.length"), done: newPassword.length >= LENGTH },
            { text: t("profile.rule.letter"), done: /[a-zA-Zа-яА-Я]/.test(newPassword) },
            { text: t("profile.rule.digit"), done: /[0-9]/.test(newPassword) },
            { text: t("profile.rule.different"), done: newPassword.length > 0 && newPassword !== password },
        ]
    }

    /* STRENGTH OF A NEW PASSWORD — THE NUMBER OF THE RULES IT KEEPS */
    strength = () => {

        /* Fields */
        const { newPassword } = this.state
        const done = this.rules().filter((item) => item.done).length

        /* Check Password */
        if (newPassword.length === 0) {
            return { level: 0, name: "", label: "" }
        }

        /* Weak */
        if (done <= 2) {
            return { level: 1, name: "weak", label: t("profile.strength.weak") }
        }

        /* Medium */
        if (done === 3) {
            return { level: 2, name: "medium", label: t("profile.strength.medium") }
        }

        /* Strong — every rule is kept and the password is longer than the minimum */
        if (newPassword.length >= 12) {
            return { level: 4, name: "strong", label: t("profile.strength.strong") }
        }

        return { level: 3, name: "good", label: t("profile.strength.good") }
    }


    /* Save Action */
    save = () => {

        /* Fields */
        const { password, newPassword, repeatPassword, waiting } = this.state

        /* Check Waiting */
        if (waiting) {
            return
        }

        /* Check Current Password */
        if (password === "") {
            this._password.error(t("profile.error.emptyCurrentPwd"))
            return
        }

        if (password.length < LENGTH) {
            this._password.error(t("profile.error.shortCurrentPwd"))
            return
        }

        /* Check New Password */
        if (newPassword === "") {
            this._newPassword.error(t("profile.error.emptyNewPwd"))
            return
        }

        if (newPassword.length < LENGTH) {
            this._newPassword.error(t("profile.error.shortNewPwd"))
            return
        }

        if (newPassword === password) {
            this._newPassword.error(t("profile.error.samePassword"))
            return
        }

        /* Check Repeat */
        if (newPassword !== repeatPassword) {
            this._repeatPassword.error(t("profile.error.mismatch"))
            return
        }

        /* Clear Errors */
        this._password.error("")
        this._newPassword.error("")
        this._repeatPassword.error("")

        /* Start Saving */
        this.setState({ waiting: true })

        /* Change Password */
        change({ password, newPassword }).then((response) => {
            if (response.status === 200) {

                /* Clear */
                this.setState({ ...EMPTY })

                /* Close Panel */
                this.close()

                Swal.fire(utils.notification(t("profile.passwordChanged"), "success"))
            }
            else if (response.status === 401) {
                this._password.error(t("profile.error.wrongPassword"))
            }
            else if (response.status === 400) {
                this._newPassword.error(t("profile.error.samePassword"))
            }
            else if (response.status === 498) {
                Swal.fire(utils.notification(t("swal.invalidKey"), "error"))
            }
            else {
                Swal.fire(utils.notification(t("swal.generic"), "error"))
            }
        }).catch(() => {
            Swal.fire(utils.notification(t("swal.generic"), "error"))
        }).finally(() => {
            this.setState({ waiting: false })
        })
    }


    /* Handle Key Down — Enter saves the form */
    handleKeyDown = (event) => {

        if (event.key === "Enter") {
            event.preventDefault()
            this.save()
        }
    }


    /* Draw Strength */
    _strength = () => {

        /* Fields */
        const { newPassword } = this.state

        /* Check Password */
        if (newPassword.length === 0) {
            return null
        }

        /* Fields */
        const strength = this.strength()

        return (
            <div className={`profile--strength ${strength.name}`}>

                <div className="profile--strength-bar">
                    {[1, 2, 3, 4].map((item) => <div className={`profile--strength-line ${item <= strength.level ? "active" : ""}`} key={`${item}`} />)}
                </div>

                <span>{strength.label}</span>

            </div>
        )
    }


    /* Draw Rules */
    _rules = () => {

        return (
            <div className="profile--rules">
                {this.rules().map((item, index) =>
                    <div className={`profile--rule ${item.done ? "done" : ""}`} key={`${index}`}>
                        <div className="profile--rule-mark"><CheckIcon size={9} /></div>
                        <span>{item.text}</span>
                    </div>
                )}
            </div>
        )
    }


    /* Draw Button */
    _button = () => {

        /* Fields */
        const { waiting } = this.state

        /* Waiting */
        if (waiting) {
            return <div className="profile--button loading"><LoadingOutlined /></div>
        }

        return <div onClick={() => this.save()} className="profile--button">{t("common.save")}</div>
    }


    render = () => {

        /* Fields */
        const { visible, password, newPassword, repeatPassword } = this.state

        return (
            <Modal
                isOpen={visible}
                onRequestClose={() => this.close()}
                className="profile--modal small"
                overlayClassName="profile--overlay"
                closeTimeoutMS={220}
            >

                <div className="profile--header">

                    <div className="profile--header-icon"><LockIcon size={17} /></div>

                    <div className="profile--header-text">
                        <h2>{t("profile.changePassword")}</h2>
                        <span>{t("profile.changePasswordSubtitle")}</span>
                    </div>

                    <div onClick={() => this.close()} className="profile--close">
                        <CloseIcon size={13} />
                    </div>

                </div>

                <div className="profile--body">

                    <div className="profile--section">

                        <Input
                            ref={(ref) => this._password = ref}
                            label={t("profile.currentPassword")}
                            value={password}
                            onChange={(event) => this.setState({ password: event.target.value })}
                            onKeyDown={this.handleKeyDown}
                            secure
                        />

                    </div>

                    <div className="profile--section">

                        <Input
                            ref={(ref) => this._newPassword = ref}
                            label={t("profile.newPassword")}
                            value={newPassword}
                            onChange={(event) => this.setState({ newPassword: event.target.value })}
                            onKeyDown={this.handleKeyDown}
                            secure
                        />

                        <Input
                            ref={(ref) => this._repeatPassword = ref}
                            label={t("profile.repeatPassword")}
                            value={repeatPassword}
                            onChange={(event) => this.setState({ repeatPassword: event.target.value })}
                            onKeyDown={this.handleKeyDown}
                            secure
                        />

                        {/* Draw Strength */}
                        {this._strength()}

                    </div>

                    {/* Draw Rules */}
                    {this._rules()}

                </div>

                <div className="profile--footer">
                    <div onClick={() => this.close()} className="profile--button ghost">{t("common.cancel")}</div>
                    {this._button()}
                </div>

            </Modal>
        )
    }

}

const mapStateToProps = (state) => ({
    language: state.language,
})

export default connect(mapStateToProps, null, null, { forwardRef: true })(Password)
